import type { PickingInfo } from "@deck.gl/core";
import { along } from "@turf/along";
import booleanEqual from "@turf/boolean-equal";
import { lineString } from "@turf/helpers";
import { length as turfLength } from "@turf/length";
import type { Feature, LineString } from "geojson";

import type { WindowDimensions } from "../app/appSlice";

import {
  hexToRGBA,
  hexToRgb,
  RGB_FORMAT_OUTPUT,
  shadeColor,
} from "@/features/utils/color";
import {
  type EdgePosition,
  type ExtendedRouteSegment,
  type FloorChange,
  FloorChangeType,
  PointType,
  type ScatterPoint,
} from "@/features/utils/types";
import { bearingBetweenCoordinates, clamp, xyToLatLng } from "@/libs/geo";
import { BoundingBox, type Building, LatLng } from "@/libs/models";
import type { Paths } from "@/libs/models/path";
import {
  INACTIVE_ROUTE_COLOR,
  STATIC_ROUTES_PATH_LAYER_ID,
} from "../directions/constants";
import type { DirectionsRoute } from "../directions/types";
import type { Props as DirectionsProps } from "../map/layers/StaticRouteLayer";
import type {
  Route,
  RouteNode,
  RouteSegment,
  RouteStep,
} from "../navigation/types";

const DEFAULT_BACKGROUND_PADDING = 2;

// Static route and navigation layer constants
export const commonTextProps = {
  backgroundPadding: [
    DEFAULT_BACKGROUND_PADDING,
    DEFAULT_BACKGROUND_PADDING,
    DEFAULT_BACKGROUND_PADDING,
    DEFAULT_BACKGROUND_PADDING,
  ],
  characterSet: "auto",
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
  fontSettings: {
    alphaCutOff: 1,
    buffer: 10,
    cutoff: 0.1,
    sdf: true,
    smoothing: 0.3,
  },
  getColor: [50, 50, 50, 255],

  lineHeight: 0.8,
  maxWidth: 500 / 64, // See https://deck.gl/docs/upgrade-guide,
  outlineColor: [255, 255, 255, 200],
  outlineWidth: 1.5,
};

const MIN_DOT_GAP = 0.05;
const MAX_DOT_GAP = 400;

/**
 * Merges all paths into only one path
 * @param paths
 * @returns
 */

export const mergePaths = (paths: ScatterPoint[][]) => {
  return paths.reduce((acc, path) => {
    return [...acc, ...path];
  }, [] as ScatterPoint[]);
};

/**
 * Combines all the segments present on each route to generate a single
 * segment
 * @param routesSegments
 * @returns
 */

export const mergeRoutesSegments = (
  routesSegments: (ExtendedRouteSegment[] | undefined)[],
  filterActive = true,
) => {
  return routesSegments.reduce((allRoutesSegments, routeSegments) => {
    if (!routeSegments) return allRoutesSegments;

    // Merge all the points on all the segments for one route
    const mergedSegments = routeSegments
      ?.filter((segment) => segment.isActive || !filterActive)
      .reduce((singleRouteSegments, segment) => {
        if (Object.keys(singleRouteSegments).length == 0) {
          singleRouteSegments = { ...segment };
        } else {
          singleRouteSegments.points = [
            ...singleRouteSegments.points,
            ...segment.points,
          ];
        }
        return singleRouteSegments;
      }, {} as RouteSegment);

    if (Object.keys(mergedSegments).length === 0) {
      return allRoutesSegments;
    }

    // Merge all the segments of all the routes
    if (Object.keys(allRoutesSegments).length === 0) {
      allRoutesSegments = mergedSegments;
    } else {
      allRoutesSegments.points = [
        ...allRoutesSegments.points,
        ...mergedSegments.points,
      ];
    }

    return allRoutesSegments;
  }, {} as RouteSegment);
};

/**
 * Checks whether two routes are the same or not.
 * @param firstRoute
 * @param secondRoute
 * @returns
 */

export const areRoutesEquivalent = (
  firstRoute: DirectionsRoute,
  secondRoute: DirectionsRoute,
) => {
  if (firstRoute.segments.length !== secondRoute.segments.length) {
    return false;
  }
  return firstRoute.segments.every((firtRouteSegment, idx) => {
    if (
      firtRouteSegment.points.length === 1 &&
      secondRoute.segments[idx].points.length === 1
    ) {
      return (
        firtRouteSegment.points[0].coordinate.latitude ==
          secondRoute.segments[idx].points[0].coordinate.latitude &&
        firtRouteSegment.points[0].coordinate.longitude ==
          secondRoute.segments[idx].points[0].coordinate.longitude
      );
    }
    if (
      firtRouteSegment.points.length !== secondRoute.segments[idx].points.length
    ) {
      return false;
    }
    const firstLine = lineString(
      firtRouteSegment.points.map((p) => [
        p.coordinate.longitude,
        p.coordinate.latitude,
      ]),
    );
    const secondLine = lineString(
      secondRoute.segments[idx].points.map((p) => [
        p.coordinate.longitude,
        p.coordinate.latitude,
      ]),
    );

    return booleanEqual(firstLine, secondLine);
  });
};

/**
 * Divides a line in d segments and selects the first point
 * on the nth segment.
 * @param line
 * @param d Number of segments
 * @param n Segment to select
 * @returns
 */
export const selectNthSegmentPoint = ({
  line,
  n = 1,
  d = 1,
}: {
  line: Feature<LineString>;
  n?: number;
  d?: number;
}) => {
  n = Math.min(n, d);
  return along(line, (n * turfLength(line, { units: "meters" })) / d, {
    units: "meters",
  });
};

/**
 * Obtains which type of object was picked based on the source layer.
 *
 * @param pickingInfo
 * @returns
 */

export const getObjectType = (pickingInfo: PickingInfo) => {
  const sourceLayerId = pickingInfo?.sourceLayer?.id;

  if (!sourceLayerId) return;

  if (sourceLayerId.includes(STATIC_ROUTES_PATH_LAYER_ID)) {
    return PointType.PATH;
  } else {
    return PointType.STEP;
  }
};

/**
 * Computes the route's distance (m) from either the first step or the sum
 * of the distances of all the steps.
 *
 * @param route
 */
export const computeDistanceFromSteps = (route?: DirectionsRoute) => {
  return Math.ceil(
    route?.steps[0]?.distanceToGoal
      ? route.steps[0].distanceToGoal
      : route?.steps.reduce((acc: any, step: any) => {
          return acc + step.distance;
        }, 0) || 0,
  );
};

/**
 * Estimates a time for a given distance (m)
 *
 * @param distance
 */

export const computeEstimatedTimeFromDistance = (distance: number) => {
  return Math.ceil((distance * 60) / 5000);
};

export const handlePathColors = (props: DirectionsProps) => {
  return {
    accentColor: hexToRgb(
      props.theme.pathAnimationColor
        ? props.theme.pathAnimationColor
        : shadeColor(props.theme.pathColor, -40),
      RGB_FORMAT_OUTPUT.ARRAY,
    ),
    arrowColor: hexToRgb(
      props.theme.arrowColor ? props.theme.arrowColor : props.theme.pathColor,
      RGB_FORMAT_OUTPUT.ARRAY,
    ),
    baseColor: hexToRgb(props.theme.pathColor, RGB_FORMAT_OUTPUT.ARRAY),
    borderColor: hexToRgb(
      props.theme.borderColor ? props.theme.borderColor : "#ffffff",
      RGB_FORMAT_OUTPUT.ARRAY,
    ),
    inactiveColor: props.theme.pathUnselectedColor
      ? hexToRGBA(props.theme.pathUnselectedColor)
      : hexToRGBA(INACTIVE_ROUTE_COLOR),
  };
};

/**
 * If segment is active we return the color after modifying the alpha channel
 * to make it translucent.
 *
 * @param color base color.
 * @param isActive controls whether alpha channel is modified
 *
 * @return Four element Uint8Array that represents the color.
 */

export const getColorBasedOnSegment = (
  color: Uint8Array,
  isSegmentActive: boolean,
): Uint8Array => {
  if (isSegmentActive) {
    return color;
  } else {
    return new Uint8Array([...color.slice(0, 3), 125]);
  }
};

/**
 * Checks if a step is contained on a segment simply checking if the origin or destination
 * coordinates on the step equal to the any point of the segment.
 *
 * @param step: RouteStep to be found.
 * @param segment
 *
 * @return Boolean as to whether or not the step is in the segment.
 */

export const segmentHasStep = (
  step: RouteStep | null | undefined,
  segment: RouteSegment,
) => {
  return (
    segment?.points.find(
      (p) =>
        (p.coordinate.latitude === step?.from.coordinate.latitude &&
          p.coordinate.longitude === step?.from.coordinate.longitude) ||
        (p.coordinate.latitude === step?.to.coordinate.latitude &&
          p.coordinate.longitude === step?.to.coordinate.longitude),
    ) !== undefined
  );
};

/**
 * Goes over steps to find the one that is equal to the given node based on
 * coordinates and floor identifier.
 * @returns Step associated with a node on the route
 */
export const getStepFromNode = (routeSteps: RouteStep[], node: RouteNode) => {
  return routeSteps.find((s) => {
    return (
      s.from.coordinate.latitude == node.coordinate.latitude &&
      s.from.coordinate.longitude == node.coordinate.longitude &&
      s.from.floorIdentifier == node.floorIdentifier
    );
  });
};

/**
 * Obtains the destination floor for a floor change on a given segment,
 * if the next segment has more than one point, we use either the "to" property
 * of the step associated with the floor change or we use the first point
 * on the next segment.
 * @returns Destination floor identifier
 */
export const getDestinatonFloorId = (
  segments: ExtendedRouteSegment[],
  routeSteps: RouteStep[],
): string => {
  const currentSegment = segments[0];

  // If this is last segment, return the  current floor as destination floor
  if (currentSegment.isLast || segments.length == 1) {
    return currentSegment.floorIdentifier;
  }

  // Origin of floor change
  const lastPointOnSegment =
    currentSegment.points[currentSegment.points.length - 1];
  // Destination of floor change
  const firstPointOnNextSegment = segments[1].points[0];
  const nextSegmentHasOnePoint = segments[1].points.length < 2;

  // If next segment has more than one point we can use the "to"
  // property to select the destination floor
  if (!nextSegmentHasOnePoint) {
    const step = getStepFromNode(routeSteps, lastPointOnSegment);
    return step?.to.floorIdentifier ?? firstPointOnNextSegment.floorIdentifier;
  }

  // Else keep searching on next segments
  return getDestinatonFloorId(segments.slice(1), routeSteps);
};

/**
 *  Computes the gap between route points given a zoom level.
 */

const computeDotGapFromZoom = (zoom: number) => {
  const dotGap = Math.E ** -((1 / 1.5) * zoom - 13.5);
  return clamp(dotGap, MIN_DOT_GAP, MAX_DOT_GAP);
};

/**
 * Generates a line for the points that conform a segment which are given by "nodes".
 * These nodes are obtained from the points on a segment.
 * The space between points depends on current zoom.
 * The points on the line are selected starting from the destination towards the origin,
 * this helps to mantain same point position on small redraws. When user is in navigation
 * we remove the closest point to him/her without redrawing the rest of them.
 *
 * Finally the points are transformed onto scatter points.
 *
 * @param nodes array of points that conform a segment.
 * @param zoom current zoom level
 *
 * @return Array of type ScatterPoint.
 */

export const calculatePathPoints = (
  nodes: ScatterPoint["coordinates"][],
  zoom: number,
  routeId: number,
  isEmbed?: boolean,
  customOffset?: number,
): ScatterPoint[] => {
  if (!nodes || nodes.length < 2) return [];
  const gap = computeDotGapFromZoom(zoom) + (customOffset || 0);

  const values = [];
  const line = lineString(nodes);
  const distance = turfLength(line, { units: "meters" });
  let offset = distance;

  // if we are in embedMode we give some more space between current location and first node in path
  while (isEmbed ? offset > 0.5 : offset > 0) {
    const p = along(line, offset, { units: "meters" });
    values.push(p);
    offset -= gap;
  }

  const scatterPoints = values.map((d, index) => {
    return {
      coordinates: [d.geometry.coordinates[0], d.geometry.coordinates[1]],
      routeId,
      speed: 12.0,
      timeStamp: values.length - index,
    } as ScatterPoint;
  });

  //if gap is bigger than distance between our 2 points => we show last one
  if (distance < gap) {
    return [
      {
        coordinates: nodes[nodes.length - 1],
        routeId,
        speed: 12.0,
        timeStamp: 1,
      } as ScatterPoint,
    ];
  }

  return scatterPoints;
};

/**
 * Obtain a list of floor changes using the steps, there is a floor change
 * if the floor id associated with the origin is different than the floor
 * id associated with the destination.
 *
 * Also obtains a mapping between the related levels associated with the
 * origin and destination which will be used to decide what kind of indication
 * nodes to paint.
 *
 * @return list of floor changes.
 */

// TODO ALBA: these should be generic indications. And they should not include
// state i.e data like inCurrentSegment
export const computeFloorChanges = ({
  routeId,
  building,
  segments,
  steps,
  isSelectedRoute,
}: {
  routeId: number;
  building: Building | null;
  segments: ExtendedRouteSegment[] | undefined;
  steps: RouteStep[] | undefined;
  isSelectedRoute: boolean;
}) => {
  if (!building || !segments || !steps) return;

  const floorChangesList = segments.reduce(
    (floorChanges: FloorChange[], seg, idx) => {
      // Check if the current floor change is in the active segment
      const inCurrentSegment = (isSelectedRoute && seg.isActive) ?? false;

      // Add destination floor changes for each new entry of the segments
      // dictionary except the first one
      if (idx > 0) {
        const firstPoint = seg.points[0];
        const previousLastPoint =
          segments[idx - 1].points[segments[idx - 1].points.length - 1];
        floorChanges.push({
          floorId: parseInt(firstPoint.floorIdentifier, 10),
          inCurrentSegment: inCurrentSegment,
          lat: firstPoint.coordinate.latitude,
          level:
            building?.getFloorById(parseInt(firstPoint.floorIdentifier, 10))
              ?.level || 0,
          lng: firstPoint.coordinate.longitude,
          relatedLevel:
            building?.getFloorById(
              parseInt(previousLastPoint.floorIdentifier, 10),
            )?.level || 0,
          routeId,
          stepId: undefined,
          type: FloorChangeType.DESTINATION,
        });
      }

      // Add origin floor changes to each new entry of the segments
      // dictionary except the last one
      if (idx < segments.length - 1) {
        const lastPoint = seg.points[seg.points.length - 1];
        const step = getStepFromNode(steps, lastPoint);
        const destinationFloor = getDestinatonFloorId(
          // Subset of segments: from current segment to last segment
          segments.slice(idx, segments.length),
          steps,
        );

        floorChanges.push({
          floorId: parseInt(lastPoint.floorIdentifier, 10),
          inCurrentSegment: inCurrentSegment,
          lat: lastPoint.coordinate.latitude,
          level:
            building?.getFloorById(parseInt(lastPoint.floorIdentifier, 10))
              ?.level || 0,
          lng: lastPoint.coordinate.longitude,
          relatedLevel:
            building?.getFloorById(parseInt(destinationFloor, 10))?.level || 0,
          routeId,
          stepId: step?.id,
          type: FloorChangeType.ORIGIN,
        });
      }

      return floorChanges;
    },
    [],
  );

  return floorChangesList;
};

/**
 * Obtains the active segment given a current step and
 * all the segments on the current floor as well.
 *
 * The array of segments have two additional properties: isActive and isLast.
 * The first one defines whether the user is focused on that segment and
 * it is only really relevant when there is more than one segment on the same
 * floor. The second one simply indicates if the current segment is the last one.
 *
 * @return currentSegments: all segments on current floor.
 */

// TODO ALBA: segments should not include state information i.e. activeSegment
export const computeCurrentSegments = (
  building: Building | null,
  route: DirectionsRoute | Route | undefined | null,
  floorId: number | null,
  inDirectionsRoute = true,
  filterByFloor = true,
) => {
  if (
    !building ||
    !route ||
    !route.segments ||
    route.segments.length == 0 ||
    !floorId
  )
    return;

  let activeSegmentIdx = 0;
  let currentSegments: ExtendedRouteSegment[] = route.segments
    .map((s, idx) => ({ ...s, isLast: idx === route.segments.length - 1 }))
    .filter((s) => !filterByFloor || parseInt(s.floorIdentifier) == floorId);

  // If there is only one segment on current floor, return directly first one
  // only on static route
  if (currentSegments.length === 1 && inDirectionsRoute) {
    currentSegments[0] = { ...currentSegments[0], isActive: true };
    return currentSegments;
  }

  if (inDirectionsRoute) {
    // Active segment is the first one for which the first point of the segment on current floor
    // and the current step coordinates matches (except last one that connects with next segment)
    activeSegmentIdx = currentSegments.findIndex((s) =>
      segmentHasStep(route.currentStep, s),
    );
    // If we find no active segment using the current step, we search any segment
    //  on the current floor
    if (activeSegmentIdx == -1) {
      activeSegmentIdx = currentSegments.findIndex(
        (s) => parseInt(s.floorIdentifier) == floorId,
      );
    }
  } else {
    // While on navigation the current floor will determine the active segment
    activeSegmentIdx = currentSegments.findIndex(
      (s) => parseInt(s.floorIdentifier) == floorId,
    );
  }

  // Add a new property to indicate that current segment is active
  currentSegments = currentSegments.map((s, idx) => ({
    ...s,
    isActive: idx === activeSegmentIdx,
  }));

  return currentSegments;
};

/**
 * Modifies the lat/lng of the route segments by transforming its cartesian coordinates instead of relying on the provided lat/lng values.
 * This is needed because SDKs/LibRoutes may compute lat/lng values using a different transformation than the one used in Situm Dashboard
 * (the one that should be used)
 *
 * @param {ExtendedRouteSegment[] | undefined} routeSegments - The array of route segments to be transformed.
 * Each segment contains points with cartesian coordinates relative to the building's layout.
 * @param {Object} bottomRightCorner - The geographic coordinates (latitude and longitude) of the building's image bottom right corner.
 * @param {Object} bottomLeftCorner - The geographic coordinates (latitude and longitude) of the building's image bottom left corner.
 * @param {Object} topLeftCorner - The geographic coordinates (latitude and longitude) of the building's image top left corner.
 * @param {number} width - The width of the building in units corresponding to the cartesian coordinates.
 * @param {number} length - The length of the building in units corresponding to the cartesian coordinates.
 *
 * @returns {ExtendedRouteSegment[] | undefined} An array of transformed route segments where each point now includes
 * the new geographic coordinates (latitude and longitude) in addition to its original properties. If the input is undefined,
 * the function returns undefined.
 */
export function transformRouteSegments(
  routeSegments: ExtendedRouteSegment[] | undefined,
  bottomRightCorner: LatLng,
  bottomLeftCorner: LatLng,
  topLeftCorner: LatLng,
  width: number,
  length: number,
  useFlatEarth = false,
): ExtendedRouteSegment[] | undefined {
  if (routeSegments == undefined) return undefined;

  if (!useFlatEarth) return routeSegments;

  return routeSegments.map((segment: ExtendedRouteSegment) => {
    const newPoints = segment.points.map((p) => {
      const coords = xyToLatLng(
        p.cartesianCoordinate.x,
        p.cartesianCoordinate.y,
        bottomRightCorner,
        bottomLeftCorner,
        topLeftCorner,
        width,
        length,
      );

      return {
        ...p,
        coordinate: { latitude: coords.lat, longitude: coords.lng },
      };
    });

    return { ...segment, points: newPoints };
  });
}

export function transformBuildingPaths(
  paths: Paths,
  bottomRightCorner: LatLng,
  bottomLeftCorner: LatLng,
  topLeftCorner: LatLng,
  width: number,
  length: number,

  useFlatEarth = false,
) {
  if (!useFlatEarth) return paths;
  const newPaths = paths.nodes.map((n) => {
    const coords = xyToLatLng(
      n.x,
      n.y,
      bottomRightCorner,
      bottomLeftCorner,
      topLeftCorner,
      width,
      length,
    );

    n.lat = coords.lat;
    n.lng = coords.lng;

    return n;
  });

  return newPaths;
}

/**
 * Computes the list of lines (one per segment) corresponding to the
 * route for the current floor so it takes into account both the current
 * segment and any other segment present on the current floor.
 *
 * Adds a control variable "isActive" to each point to control how to render
 * each type of point.
 *
 * @returns An array of points to be rendered.
 */

// TODO ALBA: these paths should not include state information i.e. isActive
export const computeRouteLine = ({
  segments,
  zoom,
  isEmbed,
  routeId = 0,
  customOffset,
}: {
  segments: ExtendedRouteSegment[] | undefined;
  zoom: number;
  isEmbed: boolean;
  routeId?: number;
  customOffset?: number;
}): ScatterPoint[][] => {
  if (!segments) return [];

  return segments.reduce((acc: ScatterPoint[][], f) => {
    let line = calculatePathPoints(
      f.points.map((p) => [p.coordinate.longitude, p.coordinate.latitude]),
      zoom,
      routeId,
      isEmbed,
      customOffset,
    );
    // if the current segment is active, add a new property
    // to decide how each point is to be rendered
    if (f.isActive) {
      line = line.map((p) => ({ ...p, isActive: true }));
    } else {
      line = line.map((p) => ({ ...p, isActive: false }));
    }
    acc.push(line);
    return acc;
  }, []);
};

const sdkToWidget = (sdkFormatData: RouteNode): EdgePosition => {
  return {
    floorId: sdkFormatData.floorIdentifier,
    lat: sdkFormatData.coordinate.latitude,
    level: undefined,
    lng: sdkFormatData.coordinate.longitude,
  };
};

/**
 * Obtains the starting position and the final position that will be
 * rendered differently
 *
 * @param segments: array of segments on current floor.
 *
 */

export const obtainEdgePositions = (
  segments: ExtendedRouteSegment[] | undefined,
) => {
  if (!segments) return {};

  const activeSegment = segments.find((s) => s.isActive);
  const lastSegment = segments.find((s) => s.isLast);

  const startPosition =
    (activeSegment && sdkToWidget(activeSegment.points[0])) || null;
  const finalPosition =
    (lastSegment &&
      sdkToWidget(lastSegment.points[lastSegment.points.length - 1])) ||
    null;

  return { finalPosition, startPosition };
};

/**
 * Obtains a new viewport that focuses on a given segment. That is a new center,
 * zoom level and angle (only on embed mode) is retrieved.
 *
 * @returns New viewport definition.
 */

export const calculateSegmentFitting = (
  floorId: number | null,
  segment: RouteSegment | undefined,
  isMobile: boolean,
  initialWindowSize: WindowDimensions,
  topHeight?: number,
  bottomHeight?: number,
) => {
  if (!floorId || !segment) {
    return null;
  }

  const points: LatLng[] = segment.points.map(
    (d) => new LatLng(d.coordinate.latitude, d.coordinate.longitude),
  );

  return calculateSegmentFittingInternal(
    floorId,
    points,
    isMobile,
    initialWindowSize,
    topHeight,
    bottomHeight,
  );
};

export const calculateArrowFitting = (
  floorId: number | null,
  points: LatLng[] | undefined,
  isMobile: boolean,
  initialWindowSize: WindowDimensions,
  topHeight?: number,
  bottomHeight?: number,
) => {
  if (!floorId || !points) {
    return null;
  }
  return calculateSegmentFittingInternal(
    floorId,
    points,
    isMobile,
    initialWindowSize,
    topHeight,
    bottomHeight,
  );
};

const calculateSegmentFittingInternal = (
  floorId: number | null,
  points: LatLng[] | undefined,
  isMobile: boolean,
  initialWindowSize: WindowDimensions,
  topHeight?: number,
  bottomHeight?: number,
) => {
  const boundingBox = new BoundingBox(points);
  const sw = boundingBox.getSW();
  const ne = boundingBox.getNE();
  const angle = bearingBetweenCoordinates(
    points[0].lat,
    points[0].lng,
    points[points.length - 1].lat,
    points[points.length - 1].lng,
  );

  let padding;
  if (isMobile) {
    const topPadding = (topHeight || 40) + 50;
    const bottomPadding = (bottomHeight || 40) + 40;
    const verticalPadding = topPadding + bottomPadding;
    padding = {
      bottom: verticalPadding < initialWindowSize.height ? bottomPadding : 0,
      left: 30,
      right: 30,
      top: verticalPadding < initialWindowSize.height ? topPadding : 0,
    };
  } else {
    const verticalPadding = 0.25 * initialWindowSize.height;
    const horizontalPadding = 0.25 * initialWindowSize.width;
    padding = {
      bottom: verticalPadding,
      left: horizontalPadding,
      right: horizontalPadding,
      top: verticalPadding,
    };
  }

  const data = {
    bounds: {
      bbox: [sw.toArray(), ne.toArray()],
      bearing: isMobile ? angle : undefined,
      padding,
    },
    floorIdentifier: floorId,
  };

  return data;
};
